import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Drawer, Form, Input, InputNumber, Modal, Select, Table, Tag, message } from "antd";
import { EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLoyaltyRewards, useLoyaltyClaims, useProducts } from "data/hooks";

const SHOP_SOURCE_COLOR = { VCE: "green", HVO: "purple" };

const LoyaltyRewards = () => {
  const { t } = useTranslation();
  const { loyaltyRewards, isLoading, createReward, updateReward, toggleRewardVisibility } = useLoyaltyRewards();
  const { loyaltyClaims } = useLoyaltyClaims();
  const { products, getProductsByShop } = useProducts();

  useEffect(() => {
    const products = getProductsByShop("VCE");
    if (products && products.length > 0) {
      const sortedProducts = [...products].sort((a, b) => (b.purchased || 0) - (a.purchased || 0));
      const output = sortedProducts.map((p, i) => `${i + 1}. ${p.name}`).join("\n");
      console.log("Top 20 products based on purchased (high to low):\n" + output);
    }
  }, [products]);

  const [searchText, setSearchText] = useState("");
  const [shopFilter, setShopFilter] = useState("all");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [drawerShopSource, setDrawerShopSource] = useState("VCE");
  const [hasVariation, setHasVariation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quantityWarningShown, setQuantityWarningShown] = useState(false);
  const [form] = Form.useForm();

  const filteredList = useMemo(() => {
    if (!loyaltyRewards) return [];
    let list = loyaltyRewards;

    if (shopFilter !== "all") {
      list = list.filter((reward) => reward.shopSource === shopFilter);
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (reward) => reward.title?.toLowerCase().includes(q) || reward.description?.toLowerCase().includes(q),
      );
    }

    return list;
  }, [loyaltyRewards, searchText, shopFilter]);

  const drawerProducts = useMemo(() => {
    return getProductsByShop(drawerShopSource);
  }, [drawerShopSource, getProductsByShop]);

  const getProductNameById = (productId, shopSource) => {
    if (!products || !productId) return null;
    const product = products.find((p) => p.id === productId && p.shopSource === shopSource);
    return product?.name || null;
  };

  const getClaimCountForReward = (rewardId, shopSource) => {
    if (!loyaltyClaims || !rewardId) return 0;
    return loyaltyClaims.filter((claim) => claim.rewardId === rewardId && claim.shopSource === shopSource).length;
  };

  const getCashedInCountForReward = (rewardId, shopSource) => {
    if (!loyaltyClaims || !rewardId) return 0;
    return loyaltyClaims.filter(
      (claim) => claim.rewardId === rewardId && claim.shopSource === shopSource && claim.cashedIn === true,
    ).length;
  };

  const openCreateDrawer = () => {
    setEditingReward(null);
    setHasVariation(false);
    setDrawerShopSource("VCE");
    form.resetFields();
    form.setFieldsValue({
      icon: "/assets/img/loyalty-program/gift.svg",
      type: "free_product",
      quantity: 1,
      shopSource: "VCE",
    });
    setDrawerVisible(true);
  };

  const openEditDrawer = (reward) => {
    setEditingReward(reward);
    setQuantityWarningShown(false);
    const hasVar = !!reward.productVariation;
    setHasVariation(hasVar);
    setDrawerShopSource(reward.shopSource);
    form.setFieldsValue({
      shopSource: reward.shopSource,
      title: reward.title,
      description: reward.description,
      points: reward.points,
      productId: reward.productId,
      quantity: reward.quantity,
      icon: reward.icon,
      type: reward.type,
      ...(hasVar && {
        variationWeight: reward.productVariation?.weight,
        variationPrice: reward.productVariation?.price,
        variationType: reward.productVariation?.type,
      }),
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setEditingReward(null);
    setHasVariation(false);
    setQuantityWarningShown(false);
    form.resetFields();
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const shopSource = editingReward ? editingReward.shopSource : values.shopSource;
      const rewardData = {
        title: values.title,
        description: values.description,
        points: values.points,
        productId: values.productId,
        quantity: values.quantity,
        icon: values.icon,
        type: values.type,
      };

      if (hasVariation) {
        rewardData.productVariation = {
          weight: values.variationWeight,
          price: values.variationPrice,
          type: values.variationType,
        };
      }

      if (editingReward) {
        await updateReward(editingReward.id, rewardData, shopSource);
        message.success(t("loyalty.reward_saved"));
      } else {
        await createReward(rewardData, shopSource);
        message.success(t("loyalty.reward_created"));
      }
      closeDrawer();
    } catch (err) {
      message.error(t("notifications.error_saving"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = (reward) => {
    const isCurrentlyVisible = reward.visible !== false;
    Modal.confirm({
      title: isCurrentlyVisible ? t("loyalty.confirm_hide_reward") : t("loyalty.confirm_show_reward"),
      onOk: async () => {
        try {
          await toggleRewardVisibility(reward.id, isCurrentlyVisible, reward.shopSource);
          message.success(t("loyalty.reward_visibility_changed"));
        } catch (err) {
          message.error(t("notifications.error_saving"));
        }
      },
    });
  };

  const handleShopSourceChange = (value) => {
    setDrawerShopSource(value);
    form.setFieldsValue({ productId: undefined });
  };

  const handleQuantityChange = (newQuantity) => {
    if (editingReward && !quantityWarningShown && newQuantity !== editingReward.quantity) {
      const claimCount = getClaimCountForReward(editingReward.id, editingReward.shopSource);
      if (claimCount > 0) {
        setQuantityWarningShown(true);
        Modal.warning({
          title: "Let op: Quantity wijziging",
          content: `Er zijn al ${claimCount} claim(s) voor deze beloning. Gebruikers die deze beloning al hebben geclaimd kunnen deze innen tegen de nieuwe quantity (${newQuantity}x), terwijl ze mogelijk een ander aantal punten hebben betaald.`,
          okText: "Begrepen",
        });
      }
    }
  };

  const tableColumns = [
    {
      title: "Shop",
      dataIndex: "shopSource",
      width: 80,
      render: (data) => <Tag color={SHOP_SOURCE_COLOR[data]}>{data}</Tag>,
    },
    {
      title: t("loyalty.reward_title"),
      dataIndex: "title",
      render: (data) => <strong>{data}</strong>,
    },
    {
      title: t("loyalty.reward_description"),
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: t("loyalty.reward_points"),
      dataIndex: "points",
      sorter: (a, b) => (a.points || 0) - (b.points || 0),
      render: (data) => (
        <Tag color="blue" style={{ fontSize: 14, padding: "2px 8px" }}>
          {data}
        </Tag>
      ),
    },
    {
      title: t("product"),
      dataIndex: "productId",
      render: (data, record) => {
        const name = getProductNameById(data, record.shopSource);
        return name || <span style={{ color: "#999" }}>{data}</span>;
      },
    },
    {
      title: t("loyalty.reward_quantity"),
      dataIndex: "quantity",
    },
    {
      title: "Aantal keer geclaimd",
      dataIndex: "id",
      render: (id, record) => {
        const count = getClaimCountForReward(id, record.shopSource);
        return <span>{count}</span>;
      },
    },
    {
      title: "Aantal keer geïnt",
      dataIndex: "id",
      render: (id, record) => {
        const count = getCashedInCountForReward(id, record.shopSource);
        return <span>{count}</span>;
      },
    },
    {
      title: t("loyalty.reward_variation"),
      dataIndex: "productVariation",
      render: (data) =>
        data ? (
          <Tag color="purple">
            {data.weight}g - €{data.price}
          </Tag>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
    {
      title: t("status"),
      dataIndex: "visible",
      render: (data) => {
        const isVisible = data !== false;
        return (
          <Tag color={isVisible ? "green" : "red"}>
            {isVisible ? t("loyalty.reward_visible") : t("loyalty.reward_not_visible")}
          </Tag>
        );
      },
      filters: [
        { text: t("loyalty.reward_visible"), value: true },
        { text: t("loyalty.reward_not_visible"), value: false },
      ],
      onFilter: (value, record) => (record.visible !== false) === value,
    },
    {
      title: t("actions"),
      dataIndex: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
          <Button
            type="text"
            icon={record.visible !== false ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => handleToggleVisibility(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            value={searchText}
            placeholder={t("loyalty.search_rewards")}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            style={{ width: 300 }}
          />
          <Select
            defaultValue="all"
            style={{ minWidth: 140 }}
            onChange={(value) => setShopFilter(value)}
            options={[
              { label: t("all"), value: "all" },
              { label: "VCE", value: "VCE" },
              { label: "HVO", value: "HVO" },
            ]}
          />
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
          {t("loyalty.create_reward")}
        </Button>
      </div>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={tableColumns}
          dataSource={filteredList}
          rowKey={(record) => `${record.shopSource}_${record.id}`}
          loading={isLoading}
          scroll={{ x: 1100 }}
        />
      </div>
      <Drawer
        title={editingReward ? `${t("loyalty.edit_reward")} (${editingReward.shopSource})` : t("loyalty.create_reward")}
        placement="right"
        width={520}
        onClose={closeDrawer}
        open={drawerVisible}
        extra={
          <Button type="primary" onClick={() => form.submit()} loading={saving}>
            {t("save")}
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {!editingReward && (
            <Form.Item name="shopSource" label="Shop" rules={[{ required: true, message: "Kies een shop" }]}>
              <Select onChange={handleShopSourceChange}>
                <Select.Option value="VCE">VCE - Vleescentrum</Select.Option>
                <Select.Option value="HVO">HVO - Halalvlees Online</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="title"
            label={t("loyalty.reward_title")}
            rules={[{ required: true, message: t("loyalty.enter_title") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label={t("loyalty.reward_description")}
            rules={[{ required: true, message: t("loyalty.enter_description") }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="points"
            label={t("loyalty.reward_points")}
            rules={[{ required: true, message: t("loyalty.enter_points") }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="type"
            label={t("loyalty.reward_type")}
            rules={[{ required: true, message: t("loyalty.enter_type") }]}
          >
            <Select>
              <Select.Option value="free_product">{t("loyalty.free_product")}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="productId"
            label={t("product")}
            rules={[{ required: true, message: t("loyalty.enter_product_id") }]}
          >
            <Select
              showSearch
              placeholder={t("form.choose_product")}
              optionFilterProp="children"
              filterOption={(input, option) => (option?.children ?? "").toLowerCase().includes(input.toLowerCase())}
            >
              {drawerProducts.map((product) => (
                <Select.Option key={product.id} value={product.id}>
                  {product.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label={t("loyalty.reward_quantity")}
            rules={[{ required: true, message: t("loyalty.enter_quantity") }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} onChange={handleQuantityChange} />
          </Form.Item>
          <Form.Item
            name="icon"
            label={t("loyalty.reward_icon")}
            rules={[{ required: true, message: t("loyalty.enter_icon") }]}
          >
            <Input />
          </Form.Item>

          <Checkbox
            checked={hasVariation}
            onChange={(e) => setHasVariation(e.target.checked)}
            style={{ marginBottom: 16 }}
          >
            {t("loyalty.has_variation")}
          </Checkbox>

          {hasVariation && (
            <>
              <Form.Item
                name="variationWeight"
                label={t("loyalty.variation_weight")}
                rules={[{ required: hasVariation, message: t("loyalty.enter_weight") }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="variationPrice"
                label={t("loyalty.variation_price")}
                rules={[{ required: hasVariation, message: t("loyalty.enter_variation_price") }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="variationType"
                label={t("loyalty.variation_type")}
                rules={[{ required: hasVariation, message: t("loyalty.enter_type") }]}
              >
                <Select>
                  <Select.Option value="WEIGHT">{t("weight")}</Select.Option>
                </Select>
              </Form.Item>
            </>
          )}
        </Form>
      </Drawer>
    </Card>
  );
};

export default LoyaltyRewards;
